import React from 'react';
import { Arrow } from 'components/Icons';
import { Carousel } from 'components/UI/Carousel';
import { Container } from 'components/UI/Container/Container';
import { Grid, GridRow, GridCol } from 'components/UI/Grid';
import { PrismicHTML, PrismicText, PrismicFixedImage } from 'components/Prismic';
import { PrismicLink } from 'components/PrismicLink';
import { prismicText } from 'helpers';
import { Heading } from 'components/LANDR/Heading';
import {
    PrismicRichTextFieldType,
    PrismicTitleFieldType,
    PrismicLinkFieldType,
    PrismicDateTextFieldType,
    SliceContext,
    PrismicImageField,
    PrismicFixedImageField,
} from '../types';
import './Blog.scss';
import { SupportedPrismicSlicesEnum } from '../enums';

type BlogItem = {
    blog_post_title: PrismicTitleFieldType;
    blog_post_image: PrismicImageField;
    blog_post_imageSharp: PrismicFixedImageField;
    blog_post_category: PrismicRichTextFieldType;
    blog_post_date: PrismicDateTextFieldType;
    blog_post_description: PrismicRichTextFieldType;
    blog_post_link: PrismicLinkFieldType;
    blog_post_alt_text: PrismicRichTextFieldType;
};

type BlogSlice = {
    type: SupportedPrismicSlicesEnum.blog;
    primary: {
        blog_title: PrismicTitleFieldType;
        blog_link_text: PrismicRichTextFieldType;
        blog_link_url: PrismicLinkFieldType;
        blog_theme: PrismicRichTextFieldType;
    };
    fields: BlogItem[];
};

type BlogProps = {
    slice: BlogSlice;
    sliceContext: SliceContext;
};

class Blog extends React.Component<BlogProps, Record<string, unknown>> {
    renderBlogTitle = () => (
        <Heading className="Blog-title" size="xl">
            <PrismicText content={this.props.slice.primary.blog_title} />
        </Heading>
    );

    renderBlogLink = () => {
        if (this.props.slice.primary.blog_link_url && this.props.slice.primary.blog_link_text) {
            return (
                <div className="Blog-linkContainer">
                    <PrismicLink
                        link={this.props.slice.primary.blog_link_url}
                        className="Blog-cta-link"
                        slice={this.props.sliceContext}
                    >
                        <div className="Grid Grid--alignCenter">
                            <p>
                                <PrismicText content={this.props.slice.primary.blog_link_text} />
                                <Arrow className="Blog-cta-arrow" />
                            </p>
                        </div>
                    </PrismicLink>
                </div>
            );
        } else {
            return null;
        }
    };

    renderBlogPosts = (theme: any) => {
        const post = (item: BlogItem, index: number) =>
            item.blog_post_link &&
            item.blog_post_image &&
            item.blog_post_date &&
            item.blog_post_title &&
            item.blog_post_description && (
                <PrismicLink
                    className="BlogPost"
                    link={item.blog_post_link}
                    key={index}
                    slice={this.props.sliceContext}
                >
                    <PrismicFixedImage
                        image={item.blog_post_imageSharp}
                        fallback={item.blog_post_image}
                        alt={prismicText(item.blog_post_alt_text)}
                        className="BlogPost-image"
                    />
                    <div className="BlogPost-meta">
                        <PrismicText component="span" className="BlogPost-category" content={item.blog_post_category} />
                        {item.blog_post_date && (
                            <time className="BlogPost-date" dateTime={item.blog_post_date}>
                                {item.blog_post_date}
                            </time>
                        )}
                        <Heading className="BlogPost-title" size="lg">
                            <PrismicText content={item.blog_post_title} />
                        </Heading>
                        <PrismicHTML content={item.blog_post_description} className="BlogPost-description" />
                    </div>
                </PrismicLink>
            );

        const posts = (view: 'mobile' | 'desktop') => {
            return this.props.slice.fields.map((item, index) => {
                if (view === 'desktop') {
                    return (
                        <GridCol
                            colSize={`4`}
                            tabletColSize={'6'}
                            mobileColSize={'12'}
                            offsetSize={`0`}
                            className="BlogPost-cell"
                            key={index}
                        >
                            {post(item, index)}
                        </GridCol>
                    );
                } else if (view === 'mobile') {
                    {
                        return post(item, index);
                    }
                }
                return null;
            });
        };

        if (this.props.slice.fields.length > 0) {
            return (
                <>
                    {/* Mobile Carousel View */}
                    <Carousel className="Blog-posts  Blog-posts--mobile" theme={theme}>
                        {posts('mobile')}
                    </Carousel>
                    {/* Desktop Grid View */}
                    <div className="Blog-posts--desktop">{posts('desktop')}</div>
                </>
            );
        }
    };

    render() {
        const theme = this.props.slice.primary.blog_theme ? this.props.slice.primary.blog_theme : 'dark';
        return (
            <div className="Blog" data-theme={theme}>
                <Container className="Blog-container" size="md">
                    <Grid>
                        <GridRow horizontalAlign={'h-start'} verticalAlign={'v-end'} className="Top-elements">
                            <GridCol colSize={`6`} mobileColSize={'12'} offsetSize={`0`}>
                                {this.renderBlogTitle()}
                            </GridCol>
                            <GridCol colSize={`6`} mobileColSize={'12'} offsetSize={`0`}>
                                {this.renderBlogLink()}
                            </GridCol>
                        </GridRow>
                        <GridRow horizontalAlign={'h-start'} verticalAlign={'v-stretch'}>
                            {this.renderBlogPosts(theme)}
                        </GridRow>
                    </Grid>
                </Container>
            </div>
        );
    }
}

export { Blog, BlogProps, BlogSlice, BlogItem };
